// Multi-Chain Token Registry
// Maps token symbols to their contract addresses across different chains
// Both Ethereum and Base use ERC20, but have different contract addresses

// Import CustomTokenPair type
import type { CustomTokenPair } from '../backend';

export type ChainId = number;

// Chain IDs
export const CHAIN_IDS = {
  ETHEREUM: 1,
  BASE: 8453,
  BSC: 56, // Binance Smart Chain
  HYPERLIQUID: 998, // Hyperliquid L1
  TRON: 728126428, // Tron (TRX) - using numeric ID for consistency
  POLYGON: 137, // Polygon (MATIC)
  ARBITRUM: 42161, // Arbitrum One
  OPTIMISM: 10, // Optimism
  AVALANCHE: 43114, // Avalanche C-Chain
} as const;

// Network name to chain ID mapping (for custom tokens)
export const NETWORK_TO_CHAIN_ID: Record<string, ChainId> = {
  'ethereum': CHAIN_IDS.ETHEREUM,
  'eth': CHAIN_IDS.ETHEREUM,
  'mainnet': CHAIN_IDS.ETHEREUM,
  'base': CHAIN_IDS.BASE,
  'bsc': CHAIN_IDS.BSC,
  'binance': CHAIN_IDS.BSC,
  'binance smart chain': CHAIN_IDS.BSC,
  'hyperliquid': CHAIN_IDS.HYPERLIQUID,
  'tron': CHAIN_IDS.TRON,
  'trx': CHAIN_IDS.TRON,
  'polygon': CHAIN_IDS.POLYGON,
  'matic': CHAIN_IDS.POLYGON,
  'arbitrum': CHAIN_IDS.ARBITRUM,
  'optimism': CHAIN_IDS.OPTIMISM,
  'avalanche': CHAIN_IDS.AVALANCHE,
  'avax': CHAIN_IDS.AVALANCHE,
  // UTXO chains (special IDs for cross-chain detection)
  'ravencoin': 10000,
  'rvn': 10000,
  'bitcoin': 10001,
  'btc': 10001,
  'litecoin': 10002,
  'ltc': 10002,
  'dogecoin': 10003,
  'doge': 10003,
  'bitcoin cash': 10004,
  'bch': 10004,
  'zcash': 10005,
  'zec': 10005,
  'dash': 10006,
  'komodo': 10007,
  'kmd': 10007,
  'qtum': 10008,
  'kaspa': 10009,
  'kas': 10009,
  // Other chains
  'solana': 9999, // Placeholder for Solana
  'sol': 9999,
  'xrp': 9998, // Placeholder for XRP
  'ripple': 9998,
  'monero': 9997, // Placeholder for Monero
  'xmr': 9997,
  'cardano': 9996, // Placeholder for Cardano
  'ada': 9996,
  'icp': 9995, // Placeholder for ICP
  'internet computer': 9995,
  'ton': 9994, // Placeholder for TON (The Open Network)
  'the open network': 9994,
  'sui': 9993, // Placeholder for SUI
};

export interface TokenInfo {
  symbol: string;
  name: string;
  decimals: number;
  addresses: Partial<Record<ChainId, string>>; // chainId -> contract address
  liquidity?: 'high' | 'medium' | 'low';
}

// Multi-chain token registry
export const TOKEN_REGISTRY: Record<string, TokenInfo> = {
  // Native UTXO tokens (no contract address, cross-chain via StealthEX/ChangeNOW)
  BTC: {
    symbol: 'BTC',
    name: 'Bitcoin',
    decimals: 8,
    addresses: {},
    liquidity: 'high',
  },
  
  LTC: {
    symbol: 'LTC',
    name: 'Litecoin',
    decimals: 8,
    addresses: {},
    liquidity: 'high',
  },
  
  DOGE: {
    symbol: 'DOGE',
    name: 'Dogecoin',
    decimals: 8,
    addresses: {},
    liquidity: 'high',
  },
  
  RVN: {
    symbol: 'RVN',
    name: 'Ravencoin',
    decimals: 8,
    addresses: {},
    liquidity: 'medium',
  },
  
  KAS: {
    symbol: 'KAS',
    name: 'Kaspa',
    decimals: 8,
    addresses: {},
    liquidity: 'high',
  },
  
  // Native EVM tokens
  ETH: {
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    addresses: {},
    liquidity: 'high',
  },
  
  BNB: {
    symbol: 'BNB',
    name: 'Binance Coin',
    decimals: 18,
    addresses: {},
    liquidity: 'high',
  },
  
  // Other native chains
  XRP: {
    symbol: 'XRP',
    name: 'Ripple',
    decimals: 6,
    addresses: {},
    liquidity: 'high',
  },
  
  SOL: {
    symbol: 'SOL',
    name: 'Solana',
    decimals: 9,
    addresses: {},
    liquidity: 'high',
  },
  
  ADA: {
    symbol: 'ADA',
    name: 'Cardano',
    decimals: 6,
    addresses: {},
    liquidity: 'high',
  },
  
  ICP: {
    symbol: 'ICP',
    name: 'Internet Computer',
    decimals: 8,
    addresses: {},
    liquidity: 'high',
  },
  
  // TON (The Open Network)
  TON: {
    symbol: 'TON',
    name: 'The Open Network',
    decimals: 9,
    addresses: {}, // Native token on TON network
    liquidity: 'high',
  },
  
  // SUI
  SUI: {
    symbol: 'SUI',
    name: 'Sui',
    decimals: 9,
    addresses: {}, // Native token on Sui network
    liquidity: 'high',
  },
  
  // Wrapped Bitcoin
  WBTC: {
    symbol: 'WBTC',
    name: 'Wrapped Bitcoin',
    decimals: 8,
    addresses: {
      [CHAIN_IDS.ETHEREUM]: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
      [CHAIN_IDS.BSC]: '0x1CE0c2827e2eF14D5C4f29a091d735A204794041', // BSC WBTC (different address!)
      // Base WBTC would have different address if it exists
    },
    liquidity: 'high',
  },
  
  // Zcash (Privacy UTXO)
  ZEC: {
    symbol: 'ZEC',
    name: 'Zcash',
    decimals: 8,
    addresses: {}, // Privacy coin, cross-chain via StealthEX/ChangeNOW
    liquidity: 'medium',
  },
  
  // Dash (UTXO)
  DASH: {
    symbol: 'DASH',
    name: 'Dash',
    decimals: 8,
    addresses: {}, // UTXO chain, cross-chain via StealthEX/ChangeNOW
    liquidity: 'medium',
  },
  
  // Bitcoin Cash (UTXO)
  BCH: {
    symbol: 'BCH',
    name: 'Bitcoin Cash',
    decimals: 8,
    addresses: {}, // UTXO chain, cross-chain via StealthEX/ChangeNOW
    liquidity: 'high',
  },
  
  // Monero (Privacy coin)
  XMR: {
    symbol: 'XMR',
    name: 'Monero',
    decimals: 12,
    addresses: {}, // Privacy coin, no EVM wrapper, cross-chain via StealthEX/ChangeNOW
    liquidity: 'high',
  },
  
  // ICP Chain-Key Tokens (Bitcoin on Internet Computer)
  CKBTC: {
    symbol: 'ckBTC',
    name: 'Chain-Key Bitcoin',
    decimals: 8,
    addresses: {
      // ckBTC exists on ICP network - canister ID would go here when needed for actual swaps
      // For now, this enables the token to be recognized in the system
    },
    liquidity: 'high',
  },
  
  // ICP Chain-Key Tokens (Ethereum on Internet Computer)
  CKETH: {
    symbol: 'ckETH',
    name: 'Chain-Key Ethereum',
    decimals: 18,
    addresses: {
      // ckETH exists on ICP network
    },
    liquidity: 'high',
  },
  
  // ICP Chain-Key Tokens (USDT on Internet Computer)
  CKUSDT: {
    symbol: 'ckUSDT',
    name: 'Chain-Key Tether',
    decimals: 6,
    addresses: {
      // ckUSDT exists on ICP network
    },
    liquidity: 'medium',
  },
  
  // ICP Chain-Key Tokens (USDC on Internet Computer)
  CKUSDC: {
    symbol: 'ckUSDC',
    name: 'Chain-Key USD Coin',
    decimals: 6,
    addresses: {
      // ckUSDC exists on ICP network
    },
    liquidity: 'high',
  },
  
  // ICP Chain-Key Tokens (Dogecoin on Internet Computer)
  CKDOGE: {
    symbol: 'ckDOGE',
    name: 'Chain-Key Dogecoin',
    decimals: 8,
    addresses: {
      // ckDOGE exists on ICP network - xevnm-gaaaa-aaaar-qafnq-cai
    },
    liquidity: 'medium',
  },
  
  // Stablecoins
  USDT: {
    symbol: 'USDT',
    name: 'Tether',
    decimals: 6,
    addresses: {
      [CHAIN_IDS.ETHEREUM]: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      [CHAIN_IDS.BSC]: '0x55d398326f99059fF775485246999027B3197955', // BSC USDT (BEP-20, different address!)
      [CHAIN_IDS.BASE]: '0xfde4C96c8593536E31F229EA8f37b2ADa2699f2', // Base USDT (different address!)
      [CHAIN_IDS.TRON]: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', // Tron USDT (TRC-20, native token)
      [CHAIN_IDS.POLYGON]: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', // Polygon USDT (ERC-20)
      [CHAIN_IDS.ARBITRUM]: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', // Arbitrum USDT (ERC-20)
      [CHAIN_IDS.OPTIMISM]: '0x94b008aA00579c1307B0EF2b499aD98a8ce58e58', // Optimism USDT (ERC-20)
      [CHAIN_IDS.AVALANCHE]: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7', // Avalanche USDT (ERC-20)
    },
    liquidity: 'high',
  },
  
  USDC: {
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    addresses: {
      [CHAIN_IDS.ETHEREUM]: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      [CHAIN_IDS.BSC]: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', // BSC USDC (BEP-20, different address!)
      [CHAIN_IDS.BASE]: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', // Base USDC (different address!)
      [CHAIN_IDS.HYPERLIQUID]: '0x4300000000000000000000000000000000000003', // Hyperliquid native USDC (Circle CCTP)
      [CHAIN_IDS.TRON]: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8', // Tron USDC (TRC-20)
      [CHAIN_IDS.POLYGON]: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', // Polygon USDC (ERC-20)
      [CHAIN_IDS.ARBITRUM]: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', // Arbitrum USDC (ERC-20)
      [CHAIN_IDS.OPTIMISM]: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', // Optimism USDC (ERC-20)
      [CHAIN_IDS.AVALANCHE]: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E', // Avalanche USDC (ERC-20)
    },
    liquidity: 'high',
  },
  
  DAI: {
    symbol: 'DAI',
    name: 'Dai Stablecoin',
    decimals: 18,
    addresses: {
      [CHAIN_IDS.ETHEREUM]: '0x6B175474E89094C44Da98b954EedeAC495271d0F',
    },
    liquidity: 'high',
  },
  
  // HYPE removed - no working swaps available (SimpleSwap/ChangeNOW APIs don't work, 1inch doesn't support Hyperliquid)
  
  // Native chain tokens (supported by SimpleSwap and ChangeNOW)
  MATIC: {
    symbol: 'MATIC',
    name: 'Polygon',
    decimals: 18,
    addresses: {}, // Native token on Polygon network
    liquidity: 'high',
  },
  
  AVAX: {
    symbol: 'AVAX',
    name: 'Avalanche',
    decimals: 18,
    addresses: {}, // Native token on Avalanche C-Chain
    liquidity: 'high',
  },
  
  ARB: {
    symbol: 'ARB',
    name: 'Arbitrum',
    decimals: 18,
    addresses: {}, // Native token on Arbitrum network
    liquidity: 'high',
  },
  
  OP: {
    symbol: 'OP',
    name: 'Optimism',
    decimals: 18,
    addresses: {}, // Native token on Optimism network
    liquidity: 'high',
  },
  
  TRX: {
    symbol: 'TRX',
    name: 'Tron',
    decimals: 6, // Tron uses 6 decimals
    addresses: {}, // Native token on Tron network
    liquidity: 'high',
  },
  
  // Popular DeFi tokens (ERC-20 on Ethereum, supported by SimpleSwap and ChangeNOW)
  LINK: {
    symbol: 'LINK',
    name: 'Chainlink',
    decimals: 18,
    addresses: {
      [CHAIN_IDS.ETHEREUM]: '0x514910771AF9Ca656af840dff83E8264EcF986CA',
    },
    liquidity: 'high',
  },
  
  UNI: {
    symbol: 'UNI',
    name: 'Uniswap',
    decimals: 18,
    addresses: {
      [CHAIN_IDS.ETHEREUM]: '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984',
    },
    liquidity: 'high',
  },
  
  AAVE: {
    symbol: 'AAVE',
    name: 'Aave',
    decimals: 18,
    addresses: {
      [CHAIN_IDS.ETHEREUM]: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9',
    },
    liquidity: 'high',
  },
  
};

/**
 * Get token contract address for a specific chain
 * @param tokenSymbol - Token symbol (e.g., 'WBTC', 'cbDOGE')
 * @param chainId - Chain ID (1 for Ethereum, 56 for BSC, 8453 for Base)
 * @param userTokenPairs - Optional array of custom token pairs
 * @returns Contract address or null if not found
 */
export function getTokenAddress(
  tokenSymbol: string, 
  chainId: ChainId,
  userTokenPairs: CustomTokenPair[] | undefined
): string | null {
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  const token = getTokenInfo(tokenSymbol, safeCustomTokenPairs);
  if (!token) return null;
  
  // Native tokens (ETH) don't have contract addresses
  if (tokenSymbol.toUpperCase() === 'ETH' && chainId === CHAIN_IDS.ETHEREUM) {
    return '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'; // Standard ETH placeholder
  }
  
  return token.addresses[chainId] || null;
}

/**
 * Get all token addresses for a token across all chains
 * @param tokenSymbol - Token symbol
 * @param userTokenPairs - Optional array of custom token pairs
 * @returns Object mapping chainId to contract address
 */
export function getTokenAddresses(
  tokenSymbol: string,
  userTokenPairs: CustomTokenPair[] | undefined
): Partial<Record<ChainId, string>> {
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  const token = getTokenInfo(tokenSymbol, safeCustomTokenPairs);
  return token?.addresses || {};
}

/**
 * Check if a token exists on a specific chain
 * @param tokenSymbol - Token symbol
 * @param chainId - Chain ID
 * @param userTokenPairs - Optional array of custom token pairs
 * @returns True if token exists on chain
 */
export function hasTokenOnChain(
  tokenSymbol: string, 
  chainId: ChainId,
  userTokenPairs: CustomTokenPair[] | undefined
): boolean {
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  const address = getTokenAddress(tokenSymbol, chainId, safeCustomTokenPairs);
  return address !== null;
}

/**
 * Get chain ID from contract address (EVM chains)
 * For EVM addresses, we default to Ethereum if no network is specified.
 * Note: This is a reasonable default since we cannot determine the chain
 * from just the address format. The AddCustomTokenModal requires network
 * selection for EVM chains to avoid this ambiguity.
 */
function getChainIdFromContractAddress(contractAddress: string): ChainId | null {
  // EVM addresses: 0x followed by 40 hex characters
  if (/^0x[a-fA-F0-9]{40}$/.test(contractAddress)) {
    return CHAIN_IDS.ETHEREUM; // Default assumption for EVM addresses
  }
  return null;
}

/**
 * Get token info (checks both registry and custom tokens)
 * @param tokenSymbol - Token symbol
 * @param userTokenPairs - Optional array of custom token pairs
 * @returns Token info or null
 */
export function getTokenInfo(
  tokenSymbol: string,
  userTokenPairs: CustomTokenPair[] | undefined
): TokenInfo | null {
  // Check registry first
  const registryToken = TOKEN_REGISTRY[tokenSymbol.toUpperCase()];
  if (registryToken) {
    return registryToken;
  }
  
  // Check custom tokens - ensure userTokenPairs is defined
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  if (safeCustomTokenPairs.length > 0) {
    const customToken = safeCustomTokenPairs.find(
      t => t.symbol.toUpperCase() === tokenSymbol.toUpperCase()
    );
    
    if (customToken) {
      // Build TokenInfo from custom token
      let chainId: ChainId | null = null;
      
      // Try network field first
      if (customToken.network) {
        const networkLower = customToken.network.toLowerCase();
        chainId = NETWORK_TO_CHAIN_ID[networkLower] || null;
      }
      
      // Try contract address (EVM chains)
      if (!chainId && customToken.contractAddress) {
        chainId = getChainIdFromContractAddress(customToken.contractAddress);
      }
      
      // Default to Ethereum if we have a contract address but no network
      if (!chainId && customToken.contractAddress) {
        chainId = CHAIN_IDS.ETHEREUM;
      }
      
      // If we have no chain ID and no contract address, return null
      // (token can't be used without network/address info)
      if (!chainId && !customToken.contractAddress) {
        return null;
      }
      
      return {
        symbol: customToken.symbol.toUpperCase(),
        name: customToken.name,
        decimals: 18, // Default for ERC-20, could be made configurable if backend supports decimals
        addresses: chainId && customToken.contractAddress
          ? { [chainId]: customToken.contractAddress }
          : {},
        liquidity: 'low', // Unknown liquidity for custom tokens
      };
    }
  }
  
  return null;
}

/**
 * Get all tokens available on a specific chain
 * @param chainId - Chain ID
 * @returns Array of token symbols available on the chain
 */
export function getTokensForChain(chainId: ChainId): string[] {
  return Object.keys(TOKEN_REGISTRY).filter(symbol => 
    hasTokenOnChain(symbol, chainId)
  );
}

/**
 * Get the primary chain ID for a token (first chain it's available on)
 * @param tokenSymbol - Token symbol
 * @param userTokenPairs - Optional array of custom token pairs
 * @returns Chain ID or null if token not found
 */
export function getTokenPrimaryChain(
  tokenSymbol: string,
  userTokenPairs: CustomTokenPair[] | undefined
): ChainId | null {
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  const token = getTokenInfo(tokenSymbol, safeCustomTokenPairs);
  if (!token) return null;
  
  // Native tokens have special handling
  if (tokenSymbol.toUpperCase() === 'ETH') {
    return CHAIN_IDS.ETHEREUM;
  }
  
  // Native UTXO tokens - map to special chain IDs for cross-chain detection
  // These are not real EVM chain IDs, but used internally for cross-chain detection
  const nativeUTXOTokens: Record<string, number> = {
    'RVN': 10000, // Special ID for Ravencoin
    'BTC': 10001, // Special ID for Bitcoin
    'LTC': 10002, // Special ID for Litecoin
    'DOGE': 10003, // Special ID for Dogecoin
    'BCH': 10004, // Special ID for Bitcoin Cash
    'ZEC': 10005, // Special ID for Zcash
    'DASH': 10006, // Special ID for Dash
    'KMD': 10007, // Special ID for Komodo
    'QTUM': 10008, // Special ID for Qtum
    'KAS': 10009, // Special ID for Kaspa
  };
  
  const nativeChainId = nativeUTXOTokens[tokenSymbol.toUpperCase()];
  if (nativeChainId) {
    return nativeChainId;
  }
  
  // For custom tokens, check if we have chain info - use already-defined safeCustomTokenPairs
  if (safeCustomTokenPairs.length > 0) {
    const customToken = safeCustomTokenPairs.find(
      t => t.symbol.toUpperCase() === tokenSymbol.toUpperCase()
    );
    
    if (customToken) {
      // Try network field
      if (customToken.network) {
        const networkLower = customToken.network.toLowerCase();
        const chainId = NETWORK_TO_CHAIN_ID[networkLower];
        if (chainId) {
          return chainId;
        }
      }
      
      // Try contract address (EVM chains)
      if (customToken.contractAddress) {
        const chainId = getChainIdFromContractAddress(customToken.contractAddress);
        if (chainId) {
          return chainId;
        }
      }
      
      // Default to Ethereum if we have a contract address
      if (customToken.contractAddress) {
        return CHAIN_IDS.ETHEREUM;
      }
    }
  }
  
  // Return first available chain
  const chainIds = Object.keys(token.addresses);
  if (chainIds.length === 0) {
    return null;
  }
  
  return parseInt(chainIds[0]) as ChainId;
}

/**
 * Get all chain IDs where a token is available
 * @param tokenSymbol - Token symbol
 * @param userTokenPairs - Optional array of custom token pairs
 * @returns Array of chain IDs
 */
export function getTokenChains(
  tokenSymbol: string,
  userTokenPairs: CustomTokenPair[] | undefined
): ChainId[] {
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  const token = getTokenInfo(tokenSymbol, safeCustomTokenPairs);
  if (!token) {
    // Check custom tokens if not in registry
    if (safeCustomTokenPairs.length > 0) {
      const customToken = safeCustomTokenPairs.find(
        t => t.symbol.toUpperCase() === tokenSymbol.toUpperCase()
      );
      
      if (customToken) {
        const chains: ChainId[] = [];
        
        // Try network field first
        if (customToken.network) {
          const networkLower = customToken.network.toLowerCase();
          const chainId = NETWORK_TO_CHAIN_ID[networkLower];
          if (chainId) {
            chains.push(chainId);
          }
        }
        
        // Try contract address (EVM chains)
        if (customToken.contractAddress) {
          const chainId = getChainIdFromContractAddress(customToken.contractAddress);
          if (chainId && !chains.includes(chainId)) {
            chains.push(chainId);
          }
        }
        
        // Default to Ethereum if we have a contract address but no network
        if (chains.length === 0 && customToken.contractAddress) {
          chains.push(CHAIN_IDS.ETHEREUM);
        }
        
        return chains.length > 0 ? chains : [];
      }
    }
    return [];
  }
  
  // Native tokens
  if (tokenSymbol.toUpperCase() === 'ETH') {
    return [CHAIN_IDS.ETHEREUM];
  }
  if (tokenSymbol.toUpperCase() === 'MATIC') {
    return [CHAIN_IDS.POLYGON];
  }
  if (tokenSymbol.toUpperCase() === 'AVAX') {
    return [CHAIN_IDS.AVALANCHE];
  }
  if (tokenSymbol.toUpperCase() === 'ARB') {
    return [CHAIN_IDS.ARBITRUM];
  }
  if (tokenSymbol.toUpperCase() === 'OP') {
    return [CHAIN_IDS.OPTIMISM];
  }
  if (tokenSymbol.toUpperCase() === 'TRX') {
    return [CHAIN_IDS.TRON];
  }
  if (tokenSymbol.toUpperCase() === 'BNB') {
    return [CHAIN_IDS.BSC]; // BNB is native token on BSC
  }
  if (tokenSymbol.toUpperCase() === 'TON') {
    return [9994]; // TON chain ID placeholder
  }
  if (tokenSymbol.toUpperCase() === 'SUI') {
    return [9993]; // SUI chain ID placeholder
  }
  
  // Native UTXO tokens - map to special chain IDs for cross-chain detection
  // These are not real EVM chain IDs, but used internally for cross-chain detection
  const nativeUTXOTokens: Record<string, number> = {
    'RVN': 10000, // Special ID for Ravencoin
    'BTC': 10001, // Special ID for Bitcoin
    'LTC': 10002, // Special ID for Litecoin
    'DOGE': 10003, // Special ID for Dogecoin
    'BCH': 10004, // Special ID for Bitcoin Cash
    'ZEC': 10005, // Special ID for Zcash
    'DASH': 10006, // Special ID for Dash
    'KMD': 10007, // Special ID for Komodo
    'QTUM': 10008, // Special ID for Qtum
    'KAS': 10009, // Special ID for Kaspa
  };
  
  const nativeChainId = nativeUTXOTokens[tokenSymbol.toUpperCase()];
  if (nativeChainId) {
    return [nativeChainId];
  }
  
  return Object.keys(token.addresses).map(id => parseInt(id) as ChainId);
}

/**
 * Check if two tokens are on different chains
 * @param fromToken - Source token symbol
 * @param toToken - Destination token symbol
 * @param fromChainId - Optional source chain ID (if provided, uses this)
 * @param toChainId - Optional destination chain ID (if provided, uses this)
 * @returns True if tokens are on different chains
 */
export function isCrossChainSwap(
  fromToken: string,
  toToken: string,
  fromChainId: ChainId | undefined,
  toChainId: ChainId | undefined,
  userTokenPairs: CustomTokenPair[] | undefined
): boolean {
  // If chain IDs are explicitly provided, use them
  if (fromChainId !== undefined && toChainId !== undefined) {
    return fromChainId !== toChainId;
  }
  
  // Otherwise, check token availability across chains - ensure userTokenPairs is defined
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  const fromChains = getTokenChains(fromToken, safeCustomTokenPairs);
  const toChains = getTokenChains(toToken, safeCustomTokenPairs);
  
  // If tokens have no overlapping chains, it's cross-chain
  const overlap = fromChains.filter(chain => toChains.includes(chain));
  return overlap.length === 0;
}

/**
 * Check if two tokens can be bridged between chains
 */
function isTokenBridgeable(
  fromToken: string,
  toToken: string,
  fromChain: ChainId,
  toChain: ChainId
): boolean {
  // Bridgeable tokens (supported by Wormhole/Across)
  // Includes wrapped UTXO tokens (WBTC only - others removed)
  // These are ERC-20 tokens on EVM chains, so Wormhole supports them
  const bridgeableTokens = [
    'ETH', 'USDC', 'USDT', 'WBTC',
    // Popular DeFi tokens
    'LINK', 'UNI', 'AAVE'
  ];
  
  const fromUpper = fromToken.toUpperCase();
  const toUpper = toToken.toUpperCase();
  
  // Both tokens must be bridgeable
  if (!bridgeableTokens.includes(fromUpper) || !bridgeableTokens.includes(toUpper)) {
    return false;
  }
  
  // Check chain support
  // Wormhole: Ethereum, Base, BSC, Hyperliquid, Arbitrum, etc.
  // Across: Ethereum L2s (Base, Arbitrum, Optimism)
  
  const wormholeChains = [CHAIN_IDS.ETHEREUM, CHAIN_IDS.BASE, CHAIN_IDS.BSC, CHAIN_IDS.HYPERLIQUID];
  const acrossChains = [CHAIN_IDS.ETHEREUM, CHAIN_IDS.BASE]; // Add more L2s as needed
  
  // Check if both chains are supported by at least one bridge
  const wormholeSupported = wormholeChains.includes(fromChain) && wormholeChains.includes(toChain);
  const acrossSupported = acrossChains.includes(fromChain) && acrossChains.includes(toChain);
  
  return wormholeSupported || acrossSupported;
}

/**
 * Get all tokens that can be swapped from a given token
 * CROSS-CHAIN DEX MODE: Shows ALL possible swap pairs
 * Based on available bridges, chains, and liquidity providers (StealthEX, ChangeNOW, Wormhole, etc.)
 * @param fromTokenSymbol - Source token symbol
 * @param currentChainId - Current chain ID (optional)
 * @returns Array of token symbols that can be swapped to
 */
export function getSwappableTokens(
  fromTokenSymbol: string,
  currentChainId: ChainId | undefined,
  userTokenPairs: CustomTokenPair[] | undefined
): string[] {
  // CRITICAL: Extract and validate userTokenPairs FIRST to prevent bundler hoisting issues
  // Use explicit undefined check pattern that bundlers can't hoist incorrectly
  // Renamed from customTokenPairs to userTokenPairs to avoid bundler conflicts
  const safeCustomTokenPairs: CustomTokenPair[] = (userTokenPairs !== undefined && Array.isArray(userTokenPairs)) ? userTokenPairs : [];
  
  const fromToken = getTokenInfo(fromTokenSymbol, safeCustomTokenPairs);
  if (!fromToken) return [];
  
  const fromChains = getTokenChains(fromTokenSymbol, safeCustomTokenPairs);
  const swappableTokens: string[] = [];
  
  // Include custom tokens in the list to check
  const allTokens: Array<{ symbol: string; tokenInfo: TokenInfo | null }> = [];
  
  // Add registry tokens
  for (const [symbol, tokenInfo] of Object.entries(TOKEN_REGISTRY)) {
    allTokens.push({ symbol, tokenInfo });
  }
  
  // Add custom tokens - use already-defined safeCustomTokenPairs
  if (safeCustomTokenPairs.length > 0) {
    for (const customToken of safeCustomTokenPairs) {
      const tokenInfo = getTokenInfo(customToken.symbol, safeCustomTokenPairs);
      // Only add if tokenInfo is valid (has network/address info)
      if (tokenInfo) {
        allTokens.push({ symbol: customToken.symbol, tokenInfo });
      }
    }
  }
  
  // Cross-chain aggregators that support most pairs
  const crossChainProviders = ['StealthEX', 'ChangeNOW'];
  
  // Tokens supported by cross-chain providers
  const crossChainSupportedTokens = [
    'BTC', 'ETH', 'LTC', 'DOGE', 'RVN', 'ZEC', 'DASH', 'BCH', 'KAS', // UTXO chains
    'XRP', 'XMR', 'SOL', // Other chains
    'USDT', 'USDC', 'DAI', // Stablecoins
    'BNB', 'WBTC', // Wrapped tokens
  ];
  
  // ICP Chain Fusion mappings
  const CHAIN_KEY_CONVERSIONS: Record<string, string[]> = {
    'BTC': ['ckBTC'],
    'ckBTC': ['BTC'],
    'ETH': ['ckETH'],
    'ckETH': ['ETH'],
    'DOGE': ['ckDOGE'],
    'ckDOGE': ['DOGE'],
  };
  
  // Get all tokens (registry + custom) - use already-defined safeCustomTokenPairs
  for (const { symbol, tokenInfo } of allTokens) {
    // Skip same token
    if (symbol.toUpperCase() === fromTokenSymbol.toUpperCase()) continue;
    
    // Skip if token info is null
    if (!tokenInfo) continue;
    
    const toChains = getTokenChains(symbol, safeCustomTokenPairs);
    
    // Check if tokens can be swapped via multiple methods:
    // 1. Same chain (direct swap via DEX aggregators like 1inch, ParaSwap)
    // 2. Cross-chain via bridges (Wormhole, Across)
    // 3. Cross-chain via aggregators (StealthEX, ChangeNOW)
    // 4. ICP Chain Fusion (BTC ↔ ckBTC, ETH ↔ ckETH, etc.)
    
    // Method 4: ICP Chain Fusion check (NEW!)
    const icpChainFusionAvailable = CHAIN_KEY_CONVERSIONS[fromTokenSymbol.toUpperCase()]?.includes(symbol.toUpperCase());
    
    // Method 1: Same chain check
    const hasOverlappingChain = fromChains.some(chain => toChains.includes(chain));
    
    // Method 2: Cross-chain via bridges
    const canBridge = fromChains.length > 0 && toChains.length > 0 && 
                      fromChains.some(fromChain => 
                        toChains.some(toChain => 
                          isTokenBridgeable(fromTokenSymbol, symbol, fromChain, toChain)
                        )
                      );
    
    // Method 3: Cross-chain via StealthEX/ChangeNOW
    // These providers support most major crypto pairs
    const supportedByCrossChainProvider = 
      crossChainSupportedTokens.includes(fromTokenSymbol.toUpperCase()) &&
      crossChainSupportedTokens.includes(symbol.toUpperCase());
    
    // Include token if ANY method works
    if (hasOverlappingChain || canBridge || supportedByCrossChainProvider || icpChainFusionAvailable) {
      swappableTokens.push(symbol);
    }
  }
  
  return swappableTokens;
}

/**
 * Check if a token has multiple chain addresses (multi-chain token)
 */
export function isMultiChainToken(tokenSymbol: string): boolean {
  const token = TOKEN_REGISTRY[tokenSymbol.toUpperCase()];
  if (!token) return false;
  const chainCount = Object.keys(token.addresses).length;
  return chainCount > 1;
}

/**
 * Get all available chain IDs for a token
 */
export function getTokenChainIds(tokenSymbol: string): number[] {
  const token = TOKEN_REGISTRY[tokenSymbol.toUpperCase()];
  if (!token) return [];
  return Object.keys(token.addresses).map(chainId => parseInt(chainId, 10));
}

/**
 * Get human-readable chain name for display
 */
export function getChainDisplayName(chainId: number): string {
  const chainNames: Record<number, string> = {
    [CHAIN_IDS.ETHEREUM]: 'Ethereum',
    [CHAIN_IDS.BSC]: 'BSC',
    [CHAIN_IDS.BASE]: 'Base',
    [CHAIN_IDS.HYPERLIQUID]: 'Hyperliquid',
    [CHAIN_IDS.TRON]: 'Tron',
    [CHAIN_IDS.POLYGON]: 'Polygon',
    [CHAIN_IDS.ARBITRUM]: 'Arbitrum',
    [CHAIN_IDS.OPTIMISM]: 'Optimism',
    [CHAIN_IDS.AVALANCHE]: 'Avalanche',
  };
  return chainNames[chainId] || `Chain ${chainId}`;
}

/**
 * Get chain abbreviation for badge display
 */
export function getChainAbbreviation(chainId: number): string {
  const abbreviations: Record<number, string> = {
    [CHAIN_IDS.ETHEREUM]: 'ETH',
    [CHAIN_IDS.BSC]: 'BSC',
    [CHAIN_IDS.BASE]: 'BASE',
    [CHAIN_IDS.HYPERLIQUID]: 'HYPE',
    [CHAIN_IDS.TRON]: 'TRX',
    [CHAIN_IDS.POLYGON]: 'MATIC',
    [CHAIN_IDS.ARBITRUM]: 'ARB',
    [CHAIN_IDS.OPTIMISM]: 'OP',
    [CHAIN_IDS.AVALANCHE]: 'AVAX',
  };
  return abbreviations[chainId] || '?';
}

/**
 * Get token standard/type for a chain (e.g., "ERC-20", "TRC-20", "BEP-20")
 */
export function getTokenStandard(chainId: number): string {
  const standards: Record<number, string> = {
    [CHAIN_IDS.ETHEREUM]: 'ERC-20',
    [CHAIN_IDS.BSC]: 'BEP-20',
    [CHAIN_IDS.BASE]: 'ERC-20',
    [CHAIN_IDS.HYPERLIQUID]: 'Native',
    [CHAIN_IDS.TRON]: 'TRC-20',
    [CHAIN_IDS.POLYGON]: 'ERC-20',
    [CHAIN_IDS.ARBITRUM]: 'ERC-20',
    [CHAIN_IDS.OPTIMISM]: 'ERC-20',
    [CHAIN_IDS.AVALANCHE]: 'ERC-20',
  };
  return standards[chainId] || 'Token';
}

/**
 * Get fee description for a chain (for UI hints)
 */
export function getChainFeeDescription(chainId: number): string {
  const descriptions: Record<number, string> = {
    [CHAIN_IDS.ETHEREUM]: 'High fees',
    [CHAIN_IDS.BSC]: 'Low fees',
    [CHAIN_IDS.BASE]: 'Low fees',
    [CHAIN_IDS.HYPERLIQUID]: 'Low fees',
    [CHAIN_IDS.TRON]: 'Very low fees',
    [CHAIN_IDS.POLYGON]: 'Low fees',
    [CHAIN_IDS.ARBITRUM]: 'Low fees',
    [CHAIN_IDS.OPTIMISM]: 'Low fees',
    [CHAIN_IDS.AVALANCHE]: 'Low fees',
  };
  return descriptions[chainId] || '';
}

